import { useState } from 'react';
import { UserPlus, ChevronDown } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { HeadcountPerson } from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useHeadcountThisMonth } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { ExpandableList } from '../expandable-list';
import { Chip, type ChipColor } from '../chip';
import { asArray } from '../widget-format';

/** A count tile that expands in place to the list of names (each → profile). */
function HeadcountTile({
  label,
  people,
  color,
}: {
  label: string;
  people: HeadcountPerson[];
  color: ChipColor;
}) {
  const [open, setOpen] = useState(false);
  const formatName = useEmployeeNameFormatter();
  const rows = asArray(people);
  const sorted = [...rows].sort((a, b) => formatName(a).localeCompare(formatName(b)));

  return (
    <div className="rounded-lg border border-border p-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2 text-left"
      >
        <span className="text-sm">{label}</span>
        <Chip color={rows.length === 0 ? 'gray' : color} className="ml-auto">
          {rows.length}
        </Chip>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open &&
        (sorted.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">No one this month.</p>
        ) : (
          <div className="mt-2">
            <ExpandableList
              items={sorted}
              getKey={(p) => p.id}
              renderItem={(p: HeadcountPerson) => (
                <Link
                  to="/employees/$id"
                  params={{ id: p.id }}
                  className="dashboard-row block truncate border-b border-border py-1.5 text-sm last:border-0 hover:underline"
                >
                  {formatName(p)}
                </Link>
              )}
            />
          </div>
        ))}
    </div>
  );
}

/**
 * `headcountThisMonth` widget — two calendar-month tiles (Onboarding /
 * Offboarded), each a count that expands in place to a profile-linked name list.
 */
export function HeadcountThisMonthWidget() {
  const { data, isLoading, isError } = useHeadcountThisMonth();

  return (
    <WidgetCard
      title="Headcount This Month"
      icon={UserPlus}
      caption="Onboarding = first working day this month · Offboarded = last working day this month (already passed)."
    >
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load this month&apos;s headcount.
        </p>
      ) : (
        <div className="grid grid-cols-2 items-start gap-3">
          <HeadcountTile label="Onboarding" people={data?.onboarding ?? []} color="green" />
          <HeadcountTile label="Offboarded" people={data?.offboarded ?? []} color="red" />
        </div>
      )}
    </WidgetCard>
  );
}
